'use client'

import { useEffect, useRef, useCallback } from 'react'

import { useSessionStorage } from './useSessionStorage'
import { SESSION_KEY } from '../constants'
import type { TabType } from './useVesselTabState'

interface ScrollPositionData {
  [tabKey: string]: number
}

/**
 * Hook to manage scroll position for vessel lists across tabs
 * - Saves scroll position in session storage
 * - Restores scroll position when staying on the same tab
 * - Resets scroll position when changing tabs
 * - Scrolls to specific vessel when IMO changes
 */
export default function useVesselScrollPosition(
  activeTab: TabType | undefined,
  containerRef: React.RefObject<HTMLElement | null>,
  imo?: string
) {
  const [scrollPositions, setScrollPositions] = useSessionStorage<ScrollPositionData>(
    SESSION_KEY.VESSEL_SCROLL_POSITION,
    {}
  )

  const previousTabRef = useRef<TabType | undefined>(activeTab)
  const isRestoringScrollRef = useRef(false)

  // Save current scroll position for the active tab
  const saveScrollPosition = useCallback(() => {
    if (!containerRef.current || isRestoringScrollRef.current || !activeTab) return
    const scrollTop = containerRef.current.scrollTop
    setScrollPositions((prev) => ({
      ...prev,
      [activeTab]: scrollTop
    }))
  }, [activeTab, containerRef, setScrollPositions])

  // Restore scroll position for the current tab
  const restoreScrollPosition = useCallback(() => {
    if (!containerRef.current || !activeTab) return

    const savedPosition = scrollPositions[activeTab] || 0
    isRestoringScrollRef.current = true

    // Use requestAnimationFrame to ensure DOM is ready
    requestAnimationFrame(() => {
      if (containerRef.current) {
        containerRef.current.scrollTop = savedPosition
      }
      // Reset flag after a short delay
      setTimeout(() => {
        isRestoringScrollRef.current = false
      }, 100)
    })
  }, [activeTab, scrollPositions, containerRef])

  // Reset scroll position when changing tabs
  const resetScrollPosition = useCallback(() => {
    if (!containerRef.current || !activeTab) return

    isRestoringScrollRef.current = true
    containerRef.current.scrollTop = 0

    // Clear the saved position for this tab
    setScrollPositions((prev) => ({
      ...prev,
      [activeTab]: 0
    }))

    setTimeout(() => {
      isRestoringScrollRef.current = false
    }, 100)
  }, [activeTab, containerRef, setScrollPositions])

  // Handle tab changes
  useEffect(() => {
    const hasTabChanged = previousTabRef.current !== activeTab

    if (hasTabChanged) {
      // Reset scroll position when changing tabs
      resetScrollPosition()
      previousTabRef.current = activeTab
    } else {
      // Restore scroll position when staying on the same tab
      restoreScrollPosition()
    }
  }, [activeTab, resetScrollPosition, restoreScrollPosition])

  // Check if element is visible in the container viewport
  const isElementVisible = useCallback((element: Element, container: Element) => {
    const elementRect = element.getBoundingClientRect()
    const containerRect = container.getBoundingClientRect()

    // Check if element is fully or mostly visible within the container
    const isVisible =
      elementRect.top >= containerRect.top &&
      elementRect.bottom <= containerRect.bottom &&
      elementRect.left >= containerRect.left &&
      elementRect.right <= containerRect.right

    // Also check if element is partially visible (at least 50% visible)
    const verticalOverlap = Math.max(
      0,
      Math.min(elementRect.bottom, containerRect.bottom) -
        Math.max(elementRect.top, containerRect.top)
    )
    const elementHeight = elementRect.bottom - elementRect.top
    const isPartiallyVisible = verticalOverlap / elementHeight >= 0.5

    return isVisible || isPartiallyVisible
  }, [])

  // Scroll to specific vessel when selectedVessel or props.vessel changes
  const scrollToVessel = useCallback(
    (targetImo: string) => {
      if (!containerRef.current) {
        return
      }

      // Find the vessel element by data-vessel-imo attribute
      const vesselElement = containerRef.current.querySelector(`[data-vessel-imo="${targetImo}"]`)

      if (vesselElement) {
        // Check if the vessel is already visible
        if (isElementVisible(vesselElement, containerRef.current)) {
          return
        }

        // Temporarily disable scroll position saving during vessel scrolling
        isRestoringScrollRef.current = true

        vesselElement.scrollIntoView({
          behavior: 'smooth',
          block: 'center' // Centers the vessel in the container
        })

        // Re-enable scroll position saving after scrolling completes
        setTimeout(() => {
          isRestoringScrollRef.current = false
        }, 1000) // Allow time for smooth scroll to complete
      }
    },
    [containerRef, isElementVisible]
  )

  // Effect to handle vessel scrolling when IMO changes
  useEffect(() => {
    if (imo && containerRef.current) {
      // Use a small delay to ensure DOM is ready and scroll position has been restored
      const scrollTimeout = setTimeout(() => {
        scrollToVessel(imo)
      }, 300)

      return () => clearTimeout(scrollTimeout)
    }
    return undefined
  }, [imo, containerRef, scrollToVessel])

  // Save scroll position when component unmounts
  useEffect(() => {
    return () => {
      saveScrollPosition()
    }
  }, [saveScrollPosition])

  // Add scroll event listener to save position during scrolling
  useEffect(() => {
    const container = containerRef.current
    if (!container) return undefined

    let scrollTimeout: NodeJS.Timeout

    const handleScroll = () => {
      // Debounce scroll position saving
      clearTimeout(scrollTimeout)
      scrollTimeout = setTimeout(() => {
        saveScrollPosition()
      }, 150)
    }

    container.addEventListener('scroll', handleScroll, { passive: true })

    return () => {
      container.removeEventListener('scroll', handleScroll)
      clearTimeout(scrollTimeout)
    }
  }, [saveScrollPosition, containerRef])

  return {
    saveScrollPosition,
    restoreScrollPosition,
    resetScrollPosition,
    scrollToVessel
  }
}
